import type { AssistantEntity } from "./types";

export const HIDDEN_PATH_PREFIXES = [
  "/pay",
  "/sign",
  "/book",
  "/embed",
  "/review",
  "/book-demo",
  "/kavoa-admin",
  "/terms",
  "/privacy",
  "/onboarding",
  "/reset-password",
  "/accept-invite",
  "/portal",
];

export function isHiddenPath(pathname: string): boolean {
  return HIDDEN_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

/** Derive the entity from the URL, if the route identifies one. */
export function entityFromPath(pathname: string): AssistantEntity | null {
  const rules: { re: RegExp; type: AssistantEntity["type"] }[] = [
    { re: /^\/clients\/([^/]+)/, type: "client" },
    { re: /^\/matters\/([^/]+)/, type: "matter" },
    { re: /^\/immigration\/([^/]+)/, type: "matter" },
    { re: /^\/real-estate\/([^/]+)/, type: "matter" },
    { re: /^\/contacts\/([^/]+)/, type: "contact" },
    { re: /^\/billing\/invoices\/([^/]+)/, type: "invoice" },
  ];
  for (const r of rules) {
    const m = pathname.match(r.re);
    if (m && m[1] && m[1] !== "new") return { type: r.type, id: m[1] };
  }
  return null;
}

export type PageKind =
  | "dashboard"
  | "client"
  | "matter"
  | "billing"
  | "tasks"
  | "calendar"
  | "time"
  | "leads"
  | "messages"
  | "generic";

export function pageKindFor(pathname: string, entity: AssistantEntity | null): PageKind {
  if (entity?.type === "client" || entity?.type === "contact") return "client";
  if (entity?.type === "matter") return "matter";
  if (entity?.type === "invoice") return "billing";
  if (pathname === "/" || pathname.startsWith("/dashboard")) return "dashboard";
  if (pathname.startsWith("/billing") || pathname.startsWith("/invoices")) return "billing";
  if (pathname.startsWith("/tasks")) return "tasks";
  if (pathname.startsWith("/calendar")) return "calendar";
  if (pathname.startsWith("/time-tracking")) return "time";
  if (pathname.startsWith("/leads")) return "leads";
  if (pathname.startsWith("/communications") || pathname.startsWith("/messages")) return "messages";
  return "generic";
}

const SUGGESTIONS: Record<PageKind, string[]> = {
  dashboard: [
    "What needs my attention today?",
    "Which tasks are overdue across my matters?",
    "What's on my calendar this week?",
    "How much unbilled time do I have?",
  ],
  client: [
    "Summarize this client's open matters",
    "Text the client that documents are ready",
    "Create a follow-up task for next Tuesday",
    "What's the trust balance for this client?",
  ],
  matter: [
    "Log 0.5 hours for today's call",
    "What's overdue on this matter?",
    "Text the client that documents are ready",
    "Draft an invoice from unbilled time",
  ],
  billing: [
    "Which invoices are overdue?",
    "How much is outstanding this month?",
    "Draft invoices for unbilled time",
    "Show payments received this week",
  ],
  tasks: [
    "What's overdue for me?",
    "What's due this week across the firm?",
    "Create a task to review the closing documents",
    "Reassign my overdue tasks to the matter team",
  ],
  calendar: [
    "What's on my calendar tomorrow?",
    "Schedule a client meeting next Thursday at 2 PM",
    "Any court dates in the next two weeks?",
    "Find a free hour on Friday",
  ],
  time: [
    "Log 1.2 hours of document review today",
    "How much unbilled time do I have?",
    "Show my time entries for this week",
    "Which matters have the most unbilled time?",
  ],
  leads: [
    "Which leads are waiting on a follow-up?",
    "Add a new lead from a phone inquiry",
    "Move the newest lead to consultation booked",
    "How many leads came in this month?",
  ],
  messages: [
    "Which clients are waiting on a reply?",
    "Summarize my unread messages",
    "Email a client a status update",
    "Text a client a reminder about tomorrow",
  ],
  generic: [
    "What needs my attention today?",
    "Which invoices are overdue?",
    "Log 0.5 hours on a matter",
    "Create a task for next week",
  ],
};

export function suggestionsFor(kind: PageKind): string[] {
  return SUGGESTIONS[kind] || SUGGESTIONS.generic;
}

export function entityLabel(type: AssistantEntity["type"]): string {
  switch (type) {
    case "client":
      return "this client";
    case "matter":
      return "this matter";
    case "contact":
      return "this contact";
    case "invoice":
      return "this invoice";
    case "task":
      return "this task";
    case "lead":
      return "this lead";
    case "user":
      return "this team member";
    default:
      return "this record";
  }
}
